// File: webelieve-frontend/src/features/intake/components/IntakePipelineView.tsx
// EF-2.1: Lead pipeline Kanban board with drag-and-drop stage transitions

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import LeadCard from './LeadCard';
import { intakeCrmApi, type LeadSummary, type LeadListFilters } from '../api/intakeCrmApi';

// --- Pipeline stage config ---

const PIPELINE_STAGES = ['new_lead', 'contacted', 'qualifying', 'converted', 'declined'] as const;
type PipelineStage = typeof PIPELINE_STAGES[number];

interface StageConfig {
  title: string;
  color: string;
  borderColor: string;
  textColor: string;
  badgeColor: string;
}

const STAGE_CONFIG: Record<PipelineStage, StageConfig> = {
  new_lead: {
    title: 'New Lead',
    color: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-200 dark:border-blue-700',
    textColor: 'text-blue-800 dark:text-blue-200',
    badgeColor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  contacted: {
    title: 'Contacted',
    color: 'bg-yellow-50 dark:bg-yellow-900/20',
    borderColor: 'border-yellow-200 dark:border-yellow-700',
    textColor: 'text-yellow-800 dark:text-yellow-200',
    badgeColor: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
  },
  qualifying: {
    title: 'Qualifying',
    color: 'bg-orange-50 dark:bg-orange-900/20',
    borderColor: 'border-orange-200 dark:border-orange-700',
    textColor: 'text-orange-800 dark:text-orange-200',
    badgeColor: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  },
  converted: {
    title: 'Converted',
    color: 'bg-green-50 dark:bg-green-900/20',
    borderColor: 'border-green-200 dark:border-green-700',
    textColor: 'text-green-800 dark:text-green-200',
    badgeColor: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  },
  declined: {
    title: 'Declined',
    color: 'bg-gray-50 dark:bg-gray-900/20',
    borderColor: 'border-gray-200 dark:border-gray-700',
    textColor: 'text-gray-600 dark:text-gray-400',
    badgeColor: 'bg-gray-100 text-gray-600 dark:bg-gray-900/40 dark:text-gray-400',
  },
};

// Map backend client status to pipeline stage for grouping
function statusToStage(status: string): PipelineStage {
  switch (status?.toLowerCase()) {
    case 'lead': return 'new_lead';
    case 'qualified': return 'qualifying';
    case 'converted': return 'converted';
    case 'rejected': return 'declined';
    case 'inactive': return 'declined';
    default: return 'new_lead';
  }
}

// --- Props ---

interface IntakePipelineViewProps {
  onLeadClick: (lead: LeadSummary) => void;
  onConvertRequest: (lead: LeadSummary) => void;
  searchQuery?: string;
  filters?: LeadListFilters;
  refreshKey?: number; // Increment to trigger refresh
}

// Stable default to prevent infinite re-render loop (new {} on each render breaks useCallback deps)
const EMPTY_FILTERS: LeadListFilters = {};

const IntakePipelineView: React.FC<IntakePipelineViewProps> = ({
  onLeadClick,
  onConvertRequest,
  searchQuery = '',
  filters = EMPTY_FILTERS,
  refreshKey = 0,
}) => {
  const [leads, setLeads] = useState<LeadSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [movingLeadId, setMovingLeadId] = useState<string | null>(null);

  // Fetch leads
  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await intakeCrmApi.listLeads({
        ...filters,
        search: searchQuery || undefined,
        limit: 200, // Get all leads for Kanban view
      });
      setLeads(result.leads);
    } catch (err) {
      console.error('[IntakePipelineView] Failed to load leads:', err);
      setError(err instanceof Error ? err.message : 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filters, refreshKey]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  // Group leads by pipeline stage
  const leadsByStage = useMemo(() => {
    const grouped: Record<PipelineStage, LeadSummary[]> = {
      new_lead: [],
      contacted: [],
      qualifying: [],
      converted: [],
      declined: [],
    };

    leads.forEach(lead => {
      const stage = statusToStage(lead.status);
      grouped[stage].push(lead);
    });

    return grouped;
  }, [leads]);

  // Handle drag-and-drop
  const onDragEnd = useCallback(async (result: DropResult) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;
    const sourceStage = source.droppableId as PipelineStage;
    const destStage = destination.droppableId as PipelineStage;

    if (sourceStage === destStage) return;

    // Find the lead being moved
    const lead = leads.find(l => l.clientId === draggableId);
    if (!lead) return;

    // If dropping to 'converted', open the conversion dialog instead of moving
    if (destStage === 'converted') {
      onConvertRequest(lead);
      return;
    }

    // Optimistic update
    setLeads(prev =>
      prev.map(l =>
        l.clientId === draggableId
          ? { ...l, status: destStage === 'new_lead' ? 'lead' : destStage }
          : l
      )
    );
    setMovingLeadId(draggableId);

    try {
      await intakeCrmApi.moveStage(draggableId, destStage);
    } catch (err) {
      console.error('[IntakePipelineView] Stage move failed:', err);
      // Revert optimistic update
      setLeads(prev =>
        prev.map(l =>
          l.clientId === draggableId ? lead : l
        )
      );
    } finally {
      setMovingLeadId(null);
    }
  }, [leads, onConvertRequest]);

  // Loading state
  if (loading && leads.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading pipeline...</span>
      </div>
    );
  }

  // Error state
  if (error && leads.length === 0) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200">{error}</p>
        <button
          onClick={loadLeads}
          className="mt-2 text-sm text-red-600 dark:text-red-400 underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: '600px' }}>
        {PIPELINE_STAGES.map((stage) => {
          const config = STAGE_CONFIG[stage];
          const stageLeads = leadsByStage[stage];

          return (
            <Droppable droppableId={stage} key={stage}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`flex-shrink-0 w-72 ${config.color} ${config.borderColor} border rounded-lg transition-shadow ${
                    snapshot.isDraggingOver ? 'ring-2 ring-lex-accent shadow-lg' : ''
                  }`}
                >
                  {/* Column header */}
                  <div className="sticky top-0 z-10 p-3 border-b border-inherit bg-inherit rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <h3 className={`text-sm font-semibold ${config.textColor}`}>
                        {config.title}
                      </h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${config.badgeColor}`}>
                        {stageLeads.length}
                      </span>
                    </div>
                  </div>

                  {/* Cards */}
                  <div className="p-2 space-y-2 min-h-[520px]">
                    {stageLeads.length === 0 ? (
                      <div className="text-center py-10">
                        <svg
                          className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600 mb-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                          />
                        </svg>
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          No leads
                        </p>
                      </div>
                    ) : (
                      stageLeads.map((lead, index) => (
                        <Draggable
                          draggableId={lead.clientId}
                          index={index}
                          key={lead.clientId}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`${
                                snapshot.isDragging
                                  ? 'ring-2 ring-lex-accent shadow-lg rotate-1'
                                  : ''
                              } ${
                                movingLeadId === lead.clientId ? 'opacity-50' : ''
                              } transition-transform`}
                            >
                              <LeadCard
                                lead={lead}
                                onClick={() => onLeadClick(lead)}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))
                    )}
                    {provided.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          );
        })}
      </div>
    </DragDropContext>
  );
};

export default IntakePipelineView;
